import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@components/ui/Card'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Camera,
  Palette,
  Package,
  ChevronDown,
  ChevronUp,
  Check,
  Gift,
  BookOpen,
  Clock,
  Sparkles,
  Users,
  ArrowRight,
  Play,
  Award
} from 'lucide-react'
import { useAuth } from '@contexts/AuthContext'
import TrainingTermsModal from '@components/training/TrainingTermsModal'

// Course curriculum data - actual modules from the course
const CURRICULUM = {
  technical: {
    title: 'Technical Skills',
    emoji: '📸',
    icon: Camera,
    description: 'Master your camera and lighting',
    modules: [
      'Exposure Triangle — Mastering Light & Balance',
      'Exposure Triangle in Real Life',
      'ISO — Light, Noise & Balance',
      'Shutter Speed',
      'Aperture (Depth of Field)',
      'White Balance — Color Accuracy in Mixed Light',
      'Focus — Capturing Sharp Emotion and Movement',
      'Cameras & Lenses — Full-Frame vs Crop',
      'Filters & Accessories — Light Shaping Tools',
      'Bokeh & Lens Rendering',
      'Metering Modes — The Camera Brain',
      'Histogram & Exposure Check',
      'Long Exposure & Motion Blur',
      'Flash Sync & Shutter Speed',
      'Sensor Cleaning & Maintenance',
      'Natural Light — Reflection, Diffusion, Direction',
      'Artificial Light',
      'Lighting Troubleshooting'
    ]
  },
  creative: {
    title: 'Creative Skills',
    emoji: '🎨',
    icon: Palette,
    description: 'Develop your artistic vision',
    modules: [
      'Moodboard for the Couple — Visual Storytelling',
      'Posing & Interaction — Capturing Connection',
      'Couple Posing Techniques',
      'Group Posing & Interaction',
      'Composition Essentials — Seeing Like a Storyteller',
      'Angles & Depth — Movement and Space',
      'Angles & Movement — Ceremonies, Dances, Emotional Beats',
      'Creative Lighting Techniques',
      'Specular Highlights — "The Jewelry of Light"',
      'Special Scenes — Low-Light, Sparklers, Rain & Night',
      'Film: Analog Look — Grain, Tones, Dynamic Range',
      'Trends & Modern Approaches'
    ]
  },
  logistic: {
    title: 'Logistic Skills',
    emoji: '📦',
    icon: Package,
    description: 'Run your wedding day smoothly',
    modules: [
      'Equipment Preparation — Choosing, Packing & Testing',
      'Photographer\'s Appearance & Mindset',
      'Communication with the Couple',
      'Coverage Strategy — Capturing the Full Story',
      'Scouting & Pre-Visualization',
      'Second Shooter — Coordination & Style Consistency'
    ]
  }
}

const FAQ_ITEMS = [
  {
    question: 'Who is this course for?',
    answer: 'This course is designed for wedding photographers at any level who want to strengthen their technical foundation, develop their creative eye, and build professional business practices. Whether you\'re just starting out or have years of experience, you\'ll find valuable insights and practical techniques.'
  },
  {
    question: 'How long do I have access?',
    answer: 'You get lifetime access to all course materials. Once you purchase, you can work through the content at your own pace and revisit any module whenever you need a refresher.'
  },
  {
    question: 'How does certification work?',
    answer: 'Complete the course and pass the assessment for each track to earn L+P Certified status — a verified credential displayed on your photographer profile. Certified photographers are chosen by couples over 70% more often than non-certified photographers on our platform.'
  },
  {
    question: 'Can I gift this course?',
    answer: 'Yes! When you checkout, you can choose to purchase this as a gift. Just enter the recipient\'s email address and an optional message. They\'ll receive access to the full course.'
  },
  {
    question: 'Is there a free preview?',
    answer: 'Yes! You can try the "Natural Light" module completely free to see if this course is right for you. Click the "Try Free Module" button to get started.'
  }
]

export default function TrainingPurchase() {
  const { user, profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  // Form state
  const [email, setEmail] = useState('')
  const [isGift, setIsGift] = useState(false)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [giftMessage, setGiftMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // UI state
  const [expandedTrack, setExpandedTrack] = useState(null)
  const [expandedFaq, setExpandedFaq] = useState(null)
  const [userHasAccess, setUserHasAccess] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email)
    }
  }, [user])

  // Check if user already has access
  useEffect(() => {
    const checkExistingAccess = async () => {
      if (!user?.id && !user?.email) return

      try {
        const response = await fetch('/api/training/check-access', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user?.id || null,
            email: user?.email || null,
            courseId: 'photography-fundamentals'
          })
        })

        const data = await response.json()
        if (data.hasAccess) {
          // User has access - enable gift mode instead of redirecting
          setUserHasAccess(true)
          setIsGift(true) // Pre-enable gift toggle
        }
      } catch (err) {
        console.error('Error checking access:', err)
      }
    }

    if (!authLoading) {
      checkExistingAccess()
    }
  }, [user, authLoading])

  const handleCheckout = async () => {
    setError(null)

    // Validate email
    const purchaserEmail = email.trim()
    if (!purchaserEmail) {
      setError('Please enter your email address')
      return
    }

    if (isGift && !recipientEmail.trim()) {
      setError('Please enter the recipient\'s email address')
      return
    }

    if (!termsAccepted) {
      setError('Please accept the Terms and Conditions to continue')
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch('/api/training/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseId: 'photography-fundamentals',
          purchaserEmail,
          userId: user?.id || null,
          isGift,
          recipientEmail: isGift ? recipientEmail.trim() : null,
          giftMessage: isGift ? giftMessage.trim() : null
        })
      })

      const data = await response.json()

      if (!response.ok) {
        if (data.error === 'ALREADY_PURCHASED') {
          setError(data.message)
        } else {
          throw new Error(data.error || 'Failed to create checkout session')
        }
        return
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const totalModules = Object.values(CURRICULUM).reduce(
    (acc, track) => acc + track.modules.length,
    0
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef2f4] to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Master Wedding Photography
              </h1>
              <p className="text-xl text-[#606b7c] mb-8">
                Real skills from working professionals. Learn the technical, creative, and logistic foundations that make great wedding photographers.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#fe395f] hover:bg-[#fe395f]/90 text-lg px-8"
                >
                  Get Full Access — $167
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/training/photography-fundamentals?category=technical&module=natural-light')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 text-lg px-8"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Try Free Module
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-white border-y border-gray-200 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-center">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-[#fe395f]" />
              <span className="text-gray-900 font-medium text-sm sm:text-base">{totalModules} In-Depth Modules</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[#fe395f]" />
              <span className="text-gray-900 font-medium text-sm sm:text-base">3 Learning Tracks</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#fe395f]" />
              <span className="text-gray-900 font-medium text-sm sm:text-base">Self-Paced Learning</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#fe395f]" />
              <span className="text-gray-900 font-medium text-sm sm:text-base">Built for Working Photographers</span>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-10 sm:py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              What You'll Learn
            </h2>
            <p className="text-base sm:text-lg text-[#606b7c] max-w-2xl mx-auto">
              Three comprehensive tracks covering everything you need to capture beautiful weddings and run a successful business.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 md:gap-8 md:grid-cols-3">
            {Object.entries(CURRICULUM).map(([key, track]) => {
              const Icon = track.icon
              const isExpanded = expandedTrack === key

              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Card className="h-full">
                    <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-[#fef2f4] to-white rounded-xl flex items-center justify-center border border-[#fe395f]/20">
                        <span className="text-xl sm:text-2xl">{track.emoji}</span>
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900">{track.title}</h3>
                        <p className="text-xs sm:text-sm text-[#606b7c]">{track.description}</p>
                      </div>
                    </div>

                    <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                      {track.modules.slice(0, isExpanded ? undefined : 4).map((module, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 sm:gap-2">
                          <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#fe395f] mt-0.5 flex-shrink-0" />
                          <span className="text-xs sm:text-sm text-gray-700">{module}</span>
                        </li>
                      ))}
                    </ul>

                    {track.modules.length > 4 && (
                      <button
                        onClick={() => setExpandedTrack(isExpanded ? null : key)}
                        className="flex items-center gap-1 text-xs sm:text-sm text-[#fe395f] hover:text-[#fe395f]/80 transition-colors"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            Show less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            See all {track.modules.length} modules
                          </>
                        )}
                      </button>
                    )}
                  </Card>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="bg-white py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6 md:gap-8 text-center">
            <div>
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#fef2f4] rounded-full mb-2 sm:mb-4">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#fe395f]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Self-Paced</h3>
              <p className="text-xs sm:text-sm text-[#606b7c]">Learn at your own speed</p>
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#fef2f4] rounded-full mb-2 sm:mb-4">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-[#fe395f]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Lifetime Access</h3>
              <p className="text-xs sm:text-sm text-[#606b7c]">Pay once, access forever</p>
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#fef2f4] rounded-full mb-2 sm:mb-4">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-[#fe395f]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Assessments</h3>
              <p className="text-xs sm:text-sm text-[#606b7c]">Test your knowledge</p>
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#fef2f4] rounded-full mb-2 sm:mb-4">
                <Award className="w-5 h-5 sm:w-6 sm:h-6 text-[#fe395f]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Certification</h3>
              <p className="text-xs sm:text-sm text-[#606b7c]">Earn L+P Certified status</p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-[#fef2f4] rounded-full mb-2 sm:mb-4">
                <Camera className="w-5 h-5 sm:w-6 sm:h-6 text-[#fe395f]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Real Scenarios</h3>
              <p className="text-xs sm:text-sm text-[#606b7c]">From actual wedding shoots</p>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout Section */}
      <section id="checkout" className="py-10 sm:py-16 md:py-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="p-5 sm:p-8">
              {/* Already has access banner */}
              {userHasAccess && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800 text-sm sm:text-base">You already have access!</p>
                      <p className="text-xs sm:text-sm text-green-700 mt-1">
                        Want to share the knowledge? Gift this course to a friend or colleague.
                      </p>
                      <button
                        onClick={() => navigate('/training/photography-fundamentals')}
                        className="text-xs sm:text-sm text-green-700 hover:text-green-800 underline mt-2"
                      >
                        Go to course →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {userHasAccess ? 'Gift This Course' : 'Get Full Access'}
                </h2>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">$167</div>
                <p className="text-sm sm:text-base text-[#606b7c]">One-time purchase · Lifetime access</p>
              </div>

              {/* Gift Toggle - hidden when user already has access (forced to gift mode) */}
              {!userHasAccess && (
                <div className="mb-4 sm:mb-6">
                  <button
                    onClick={() => setIsGift(!isGift)}
                    className={`w-full flex items-center justify-between p-3 sm:p-4 rounded-lg border-2 transition-colors ${
                      isGift
                        ? 'border-[#fe395f] bg-[#fef2f4]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Gift className={`w-4 h-4 sm:w-5 sm:h-5 ${isGift ? 'text-[#fe395f]' : 'text-gray-400'}`} />
                      <span className={`font-medium text-sm sm:text-base ${isGift ? 'text-gray-900' : 'text-gray-600'}`}>
                        This is a gift
                      </span>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isGift ? 'border-[#fe395f] bg-[#fe395f]' : 'border-gray-300'
                      }`}
                    >
                      {isGift && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isGift ? 'Your email' : 'Email address'}
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={!!user?.email}
                  />
                  {user?.email && (
                    <p className="text-xs text-[#606b7c] mt-1">
                      Signed in as {user.email}
                    </p>
                  )}
                </div>

                <AnimatePresence>
                  {isGift && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Recipient's email
                        </label>
                        <Input
                          type="email"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          placeholder="recipient@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Gift message (optional)
                        </label>
                        <textarea
                          value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          placeholder="Add a personal message..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#fe395f] focus:border-transparent resize-none"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Terms and Conditions Checkbox */}
              <div className="mb-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-[#fe395f] focus:ring-[#fe395f] border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-[#fe395f] hover:text-[#fe395f]/80 underline"
                    >
                      Terms and Conditions
                    </button>
                    {' '}for this training product.
                  </span>
                </label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Checkout Button */}
              <Button
                onClick={handleCheckout}
                disabled={isLoading}
                className="w-full bg-[#fe395f] hover:bg-[#fe395f]/90 text-lg py-6"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    {isGift ? 'Purchase Gift' : 'Get Full Access'}
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </Button>

              <p className="text-xs text-center text-[#606b7c] mt-4">
                Secure checkout powered by Stripe
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {FAQ_ITEMS.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-4 pb-4 text-[#606b7c]">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-[#fe395f] to-[#ff6b89]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Level Up Your Photography?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Join photographers who are building real skills with practical, actionable training.
          </p>
          <button
            onClick={() => document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-white text-[#fe395f] hover:bg-gray-100 text-lg px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
          >
            Get Started — $167
          </button>
        </div>
      </section>

      {/* Terms Modal */}
      <TrainingTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  )
}
